import queryString from 'query-string';

import { request } from '../request';

export interface Location {
  latitude: number;
  longitude: number;
}

export interface Measurement {
  id: string;
  value: number | string | Record<string, unknown> | null;
  location: Location | null;
  unit: { id: number; name: string } | null;
}

export const getTrialMeasurements = (
  trialId: string,
  params: {
    trait_codes?: string[];
    site_codes?: string[];
    start_date?: string;
    end_date?: string;
    user_emails?: string[];
    has_location?: boolean;
  }
): Promise<Measurement[]> => {
  const url = `/api/v2/trials/${trialId}/measurements/?${queryString.stringify(params)}`;
  return request('GET', url, {}, { injectTenant: true, version: 2 });
};

export interface DerivedMeasurement {
  id: string;
  value: string | null;
  location: Location | null;
  unit_name: string | null;
}

export const getTrialDerivedMeasurements = (
  trialId: string,
  params: {
    trait_codes?: string[];
    site_codes?: string[];
    has_location?: boolean;
    start_date?: string;
    end_date?: string;
    user_emails?: string[];
  }
): Promise<DerivedMeasurement[]> => {
  const url = `/api/v2/trials/${trialId}/derived-measurements/?${queryString.stringify(params)}`;
  return request('GET', url, {}, { injectTenant: true, version: 2 });
};

export interface HeatmapMeasurement {
  location: Location | null;
  value: number | null;
  unitName: string | null;
}

interface HeatmapMeasurementParams {
  trait_codes?: string[];
  site_codes?: string[];
  start_date?: string;
  end_date?: string;
  user_emails?: string[];
  has_location?: boolean;
}

/**
 * Heatmap measurements for a trait, normalized to a single shape so the consumer
 * does not care whether the values are stored (`/measurements/`) or derived
 * (`/derived-measurements/`). The source-specific differences (response shape,
 * numeric parsing, unit field) are handled here; if the two endpoints are ever
 * unified, only this function changes, not the heatmap screen.
 */
export const getTrialHeatmapMeasurements = (
  trialId: string,
  isDerived: boolean,
  params: HeatmapMeasurementParams
): Promise<HeatmapMeasurement[]> => {
  if (isDerived) {
    return getTrialDerivedMeasurements(trialId, params).then((measurements) =>
      measurements.map((measurement) => {
        const value = measurement.value !== null ? Number.parseFloat(measurement.value) : null;
        return {
          location: measurement.location,
          value: value !== null && !Number.isNaN(value) ? value : null,
          unitName: measurement.unit_name,
        };
      })
    );
  }
  return getTrialMeasurements(trialId, params).then((measurements) =>
    measurements.map((measurement) => ({
      location: measurement.location,
      value: typeof measurement.value === 'number' ? measurement.value : null,
      unitName: measurement.unit?.name ?? null,
    }))
  );
};
